import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import './Horarios.css' // Importamos su CSS exclusivo

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']

export default function Horarios({ toast, comercioId }) {
  const [horarios, setHorarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [modalSalirVisible, setModalSalirVisible] = useState(false)

  const cargar = useCallback(async () => {
    if (!comercioId) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('horarios_atencion')
        .select('*')
        .eq('comercio_id', comercioId)
        .order('id')
        
      if (error) {
        console.error("Error cargando horarios:", error.message)
      } else if (data && data.length > 0) {
        setHorarios(data.map(h => ({
          ...h,
          uid: h.id.toString(),
          hora_apertura: h.hora_apertura?.slice(0, 5),
          hora_cierre: h.hora_cierre?.slice(0, 5),
        })))
      } else { 
        setHorarios([]) 
      }
    } catch { 
      console.error("Excepción al cargar horarios")
    } finally { 
      setLoading(false)
      setIsDirty(false)
    }
  }, [comercioId])

  useEffect(() => { cargar() }, [cargar])

  // Bloquear el cierre o recarga de la pestaña si hay cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = 'Tenés cambios sin guardar. ¿Estás seguro de que querés salir?'
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // LÓGICA DE COPIAR LUNES A TODA LA SEMANA
  const copiarLunes = () => {
    const franjasLunes = horarios.filter(h => h.dia_semana === 'Lunes')
    if (franjasLunes.length === 0) {
      toast('Primero configurá al menos un horario el Lunes', 'error')
      return
    }

    const DIAS_HABILES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes']
    const nuevosHorarios = horarios.filter(h => h.dia_semana === 'Sábado' || h.dia_semana === 'Domingo')

    DIAS_HABILES.forEach(dia => {
      if (dia === 'Lunes') {
        nuevosHorarios.push(...franjasLunes)
      } else {
        franjasLunes.forEach(f => {
          nuevosHorarios.push({
            ...f,
            id: undefined,
            uid: Math.random().toString(36).substring(2),
            dia_semana: dia
          })
        })
      }
    })
    
    setHorarios(nuevosHorarios)
    setIsDirty(true)
    toast('Horarios aplicados de Lunes a Viernes. ¡No olvides guardar!', 'success')
  }

  const agregarFranja = (dia) => {
    setHorarios(prev => [...prev, {
      uid: Math.random().toString(36).substring(2),
      dia_semana: dia, 
      hora_apertura: '09:00', 
      hora_cierre: '18:00', 
      activo: true, 
      comercio_id: comercioId
    }])
    setIsDirty(true)
  }

  // Se eliminó el cartel nativo de confirmación y ahora activa la barra de cambios sin guardar
  const eliminarFranja = async (uid, dbId) => {
    if (dbId) {
      await supabase.from('horarios_atencion').delete().eq('id', dbId)
    }
    setHorarios(prev => prev.filter(h => h.uid !== uid))
    setIsDirty(true)
  }

  const actualizar = (uid, field, value) => {
    setHorarios(prev => prev.map(h => h.uid === uid ? { ...h, [field]: value } : h))
    setIsDirty(true)
  }

  const guardar = async () => {
    setGuardando(true)
    toast('Sincronizando horarios...', 'success')
    try {
      const payload = horarios.map(h => ({
        ...(h.id ? { id: h.id } : {}),
        comercio_id: comercioId,
        dia_semana: h.dia_semana,
        activo: h.activo,
        hora_apertura: h.hora_apertura?.length === 5 ? `${h.hora_apertura}:00` : h.hora_apertura,
        hora_cierre: h.hora_cierre?.length === 5 ? `${h.hora_cierre}:00` : h.hora_cierre
      }))
      const { error } = await supabase.from('horarios_atencion').upsert(payload)
      
      if (error) {
        console.error("Error guardando:", error.message)
        toast('Error al guardar', 'error')
        return
      }
      
      toast('Configuración guardada exitosamente', 'success')
      setIsDirty(false)
      setModalSalirVisible(false)
      cargar()
    } catch { 
      toast('Error de conexión al guardar', 'error') 
    } finally {
      setGuardando(false)
    }
  }

  const descartarCambios = () => {
    cargar()
    setModalSalirVisible(false)
  }

  if (loading) {
    return (
      <div className="horarios-loading">
        <svg className="horarios-spinner" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4"></circle>
        </svg>
        <p>Cargando cronograma...</p>
      </div>
    )
  }

  return (
    <div className="horarios-container" style={{ paddingBottom: '100px' }}>
      <div className="horarios-header">
        <div className="horarios-title-group">
          <h1 className="horarios-title">Cronograma de Atención</h1>
          <p className="horarios-subtitle">Define cuándo estará activo tu asistente virtual.</p>
        </div>
        {horarios.length > 0 && (
          <button onClick={copiarLunes} className="btn-horarios-copy">
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            Copiar Lunes a todos los días
          </button>
        )}
      </div>

      <div className="horarios-list">
        {DIAS_SEMANA.map(dia => {
          const turnos = horarios.filter(h => h.dia_semana === dia)
          const estaCerrado = turnos.length === 0 || turnos.every(t => !t.activo)

          return (
            <div key={dia} className={`horarios-card ${estaCerrado ? 'cerrado' : ''}`}>
              <div className="horarios-card-inner">
                
                <div className="horarios-day-info">
                  <div className="horarios-day-icon">
                    {estaCerrado ? (
                      <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    )}
                  </div>
                  <span className="horarios-day-name">{dia}</span>
                </div>

                <div className="horarios-slots-group">
                  {turnos.map(t => (
                    <div key={t.uid} className="horarios-slot">
                      <input 
                        type="time" 
                        className="horarios-input"
                        value={t.hora_apertura} 
                        onChange={e => actualizar(t.uid, 'hora_apertura', e.target.value)}
                      />
                      <span className="horarios-separator">—</span>
                      <input 
                        type="time" 
                        className="horarios-input"
                        value={t.hora_cierre} 
                        onChange={e => actualizar(t.uid, 'hora_cierre', e.target.value)}
                      />
                      
                      <button onClick={() => eliminarFranja(t.uid, t.id)} className="btn-horarios-delete" title="Eliminar turno">
                        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                      </button>
                    </div>
                  ))}
                  {turnos.length === 0 && (
                    <span className="horarios-empty-text">Sin horarios configurados</span>
                  )}
                </div>

                <div className="horarios-actions">
                  <button onClick={() => agregarFranja(dia)} className={estaCerrado ? 'btn-horarios-enable' : 'btn-horarios-add'}>
                    {estaCerrado ? 'Habilitar día' : '+ Turno'}
                  </button>
                </div>
                
              </div>
            </div>
          )
        })}
      </div>

      {/* Barra flotante fija de guardado - se muestra si isDirty es true */}
      <div style={{
        position: 'fixed',
        bottom: isDirty || guardando ? '24px' : '-100px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#0f172a',
        padding: '14px 28px',
        borderRadius: '50px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        zIndex: 999,
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        border: '1px solid #334155',
        width: 'max-content'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setModalSalirVisible(true)}>
          <span style={{ animation: 'pulse 2s infinite', height: '8px', width: '8px', backgroundColor: '#eab308', borderRadius: '50%' }} />
          <span style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: '500' }}>Tenés cambios sin guardar</span>
        </div>
        
        <button 
          onClick={guardar}
          disabled={guardando} 
          style={{ 
            padding: '8px 18px', 
            borderRadius: '30px', 
            fontSize: '13px', 
            cursor: 'pointer',
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            fontWeight: '500'
          }}
        >
          {guardando ? 'Guardando...' : 'Guardar configuración'}
        </button>
      </div>

      {/* Modal Moderno para advertencia de Cambios Sin Guardar */}
      {modalSalirVisible && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          backgroundColor: 'rgba(15, 23, 42, 0.6)', 
          backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', 
          zIndex: 9999 
        }}>
          <div className="animate-fade-in" style={{ 
            background: 'var(--bg-card, #1e293b)', 
            borderRadius: '12px', 
            padding: '1.5rem', 
            width: '90%', 
            maxWidth: '400px', 
            border: '1px solid var(--border-color, #334155)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' 
          }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', color: 'var(--text-main, #f8fafc)', fontWeight: '600' }}>
              Cambios sin guardar
            </h4>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: 'var(--text-muted, #94a3b8)', lineHeight: '1.5' }}>
              Modificaste configuraciones que aún no fueron consolidadas. ¿Qué deseas hacer con las ediciones actuales?
            </p>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                onClick={descartarCambios}
                style={{
                  background: 'transparent',
                  border: '1px solid #ef4444',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  color: '#ef4444',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Descartar cambios
              </button>
              <button 
                onClick={() => setModalSalirVisible(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color, #334155)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  color: 'var(--text-muted, #94a3b8)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Seguir editando
              </button>
              <button 
                onClick={guardar}
                disabled={guardando}
                style={{
                  background: '#10b981',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                {guardando ? 'Guardando...' : 'Guardar ahora'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .5; }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  )
}